import Card, { CardProps } from 'app/src/kit/Card'
import Text from 'app/src/kit/Text'
import isNil from 'lodash-es/isNil'
import { transparentize } from 'polished'
import { ReactNode } from 'react'
import { useTheme } from 'styled-components'
import Icon from './Icon'
import Icons from './Icons'

interface InfoCardProps extends CardProps {
  message?: string | ReactNode
  title?: string
  type?: 'info' | 'help' | 'tip' | 'danger' | 'warning' | 'success'
  variant?: 'sm' | 'md'
}

/**
 * Full width text space to explain/present information to the user
 */
const InfoCard: FC<InfoCardProps> = ({
  children,
  $mb = 3,
  message,
  title,
  type = 'info',
  variant = 'md',
  ...props
}) => {
  const { colors } = useTheme()

  let color: string = colors.grayscale.black
  let borderColor: string = colors.grayscale.black
  let background: string = transparentize(0.95)(colors.grayscale.black)

  let Icn: typeof Icon = Icons.InfoCircle

  switch (type) {
    case 'danger':
      color = colors.feedback.error['500']
      borderColor = colors.feedback.error['500']
      background = colors.feedback.error['200']
      Icn = Icons.AlertTriangle
      break

    case 'tip':
      color = colors.primary['800']
      borderColor = colors.primary['500']
      background = colors.primary['200']
      Icn = Icons.Lightbulb
      break

    case 'warning':
      color = colors.feedback.warning['800']
      borderColor = colors.feedback.warning['500']
      background = colors.feedback.warning['200']
      Icn = Icons.AlertTriangle
      break

    case 'help':
      color = colors.tertiary['800']
      borderColor = colors.tertiary['500']
      background = colors.tertiary['200']
      Icn = Icons.Help
      break

    case 'success':
      color = colors.feedback.success['800']
      borderColor = colors.feedback.success['500']
      background = colors.feedback.success['200']
      Icn = Icons.CheckCircle
      break
  }

  return (
    <Card
      $width='100%'
      $maxWidth='100%'
      $minHeight='auto'
      $mb={$mb}
      $backgroundColor={background}
      $borderColor={borderColor}
      $borderWidth='rg'
      $gap='md'
      $alignItems={isNil(title) ? 'center' : 'flex-start'}
      $flexDirection={variant === 'md' ? 'column' : 'row'}
      $p={variant === 'md' ? 'lg' : 'md'}
      $py={variant === 'md' ? 'lg' : 'sm'}
      {...props}
    >
      <Flex $gap='sm' $alignItems='center'>
        <Icn $size='lg' $color={color} />
        {!isNil(title) ? (
          <Text
            $lineHeight='none'
            $fontSize='md'
            $fontWeight='medium'
            $color={color}
            style={{ flex: 1 }}
          >
            {title}
          </Text>
        ) : null}
      </Flex>

      {typeof message === 'string' ? (
        <Text $color={color} $fontSize={variant === 'md' ? 'rg' : 'sm'}>
          {message}
        </Text>
      ) : isNil(message) ? null : (
        message
      )}

      {children}
    </Card>
  )
}

export default InfoCard
